/**
 * PATH: apps/ai/src/pages/reports/types.ts
 * PURPOSE: Types and template data for AI reports page
 */

import { FileText, PieChart, TrendingUp, DollarSign, Briefcase } from 'lucide-react';

export interface ParameterConfig {
  id: string;
  label: string;
  type: 'date-range' | 'select' | 'multi-select' | 'text' | 'number';
  required?: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: string;
}

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon: typeof FileText;
  color: string;
  category: 'portfolio' | 'job' | 'financial' | 'custom';
  parameters: ParameterConfig[];
  estimatedTime: string;
}

export interface GeneratedReport {
  id: string;
  templateId: string;
  name: string;
  status: 'generating' | 'completed' | 'failed';
  progress?: number;
  createdAt: Date;
  downloadUrl?: string;
  qdriveUrl?: string;
  data?: Record<string, unknown>;
}

export const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'portfolio-health', name: 'Portfolio Health Report',
    description: 'Comprehensive overview of all active jobs, margins, and key metrics',
    icon: PieChart, color: 'from-teal-500 to-emerald-600', category: 'portfolio', estimatedTime: '~30 seconds',
    parameters: [
      { id: 'dateRange', label: 'Date Range', type: 'select', required: true, options: [{ value: 'last-7-days', label: 'Last 7 Days' }, { value: 'last-30-days', label: 'Last 30 Days' }, { value: 'last-quarter', label: 'Last Quarter' }, { value: 'ytd', label: 'Year to Date' }, { value: 'custom', label: 'Custom Range' }], defaultValue: 'last-30-days' },
      { id: 'includeForecasts', label: 'Include Forecasts', type: 'select', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }], defaultValue: 'yes' },
    ],
  },
  {
    id: 'job-performance', name: 'Job Detailed KPI Report',
    description: 'Detailed analysis of individual job performance and profitability',
    icon: Briefcase, color: 'from-blue-500 to-indigo-600', category: 'job', estimatedTime: '~45 seconds',
    parameters: [
      { id: 'jobFilter', label: 'Jobs to Include', type: 'select', required: true, options: [{ value: 'all-active', label: 'All Active Jobs' }, { value: 'at-risk', label: 'At-Risk Jobs Only' }, { value: 'completed-recent', label: 'Recently Completed' }], defaultValue: 'all-active' },
      { id: 'sortBy', label: 'Sort By', type: 'select', options: [{ value: 'margin', label: 'Margin (High to Low)' }, { value: 'value', label: 'Contract Value' }, { value: 'completion', label: 'Completion %' }], defaultValue: 'margin' },
    ],
  },
  {
    id: 'outstanding-invoices', name: 'Outstanding Invoices Report',
    description: 'Aged receivables analysis with collection recommendations',
    icon: DollarSign, color: 'from-amber-500 to-orange-600', category: 'financial', estimatedTime: '~20 seconds',
    parameters: [
      { id: 'ageBrackets', label: 'Age Brackets', type: 'select', options: [{ value: 'standard', label: 'Standard (30/60/90+)' }, { value: 'detailed', label: 'Detailed (7/14/30/60/90+)' }], defaultValue: 'standard' },
      { id: 'includeRecommendations', label: 'AI Collection Recommendations', type: 'select', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }], defaultValue: 'yes' },
    ],
  },
  {
    id: 'margin-analysis', name: 'Margin Analysis',
    description: 'Deep dive into margin trends, variations, and improvement opportunities',
    icon: TrendingUp, color: 'from-purple-500 to-pink-600', category: 'financial', estimatedTime: '~40 seconds',
    parameters: [
      { id: 'compareAgainst', label: 'Compare Against', type: 'select', options: [{ value: 'budget', label: 'Original Budget' }, { value: 'forecast', label: 'Current Forecast' }, { value: 'historical', label: 'Historical Average' }], defaultValue: 'budget' },
      { id: 'breakdown', label: 'Breakdown By', type: 'select', options: [{ value: 'cost-code', label: 'Cost Code' }, { value: 'stage', label: 'Project Stage' }, { value: 'vendor', label: 'Vendor/Subcontractor' }], defaultValue: 'cost-code' },
    ],
  },
];
